import type { TSelectedTaggedBlock } from '@modules/documents';
import { TBlockTag } from '@modules/users';

/**
 * Filters tags for display in dropdowns/selectors.
 * Shows all active tags plus any deactivated tag that is currently selected,
 * so users can see and clear their existing selection.
 *
 * @param allTags - All available tags from organization
 * @param selectedTagIds - Set of currently selected tag IDs (for multi-tag contexts like TaggedSnippets)
 *                         or a single tag ID string (for single-tag contexts like InlineBlockConfigDialog)
 */
export const filterTagsForDisplay = (allTags: TBlockTag[], selectedTagIds?: Set<string> | string): TBlockTag[] => {
  const activeTags = allTags.filter((t) => t.active);

  // Handle both single string and Set<string> for flexibility
  const selectedSet =
    typeof selectedTagIds === 'string'
      ? new Set(selectedTagIds ? [selectedTagIds] : [])
      : (selectedTagIds ?? new Set());

  const deactivatedWithSelection = allTags.filter((t) => !t.active && selectedSet.has(t.id));
  return [...activeTags, ...deactivatedWithSelection];
};

/** Resolves a block tag ID to its display label. Falls back to the raw ID if no match. */
export const getBlockTagLabel = (blockTags: TBlockTag[] | undefined, blockTagId: string): string =>
  blockTags?.find((t) => t.id === blockTagId)?.label ?? blockTagId;

/** Finds the selected tagged block entry for a given tag ID. */
export const findSelectedTaggedBlock = (
  blocks: TSelectedTaggedBlock[],
  blockTagId: string
): TSelectedTaggedBlock | undefined => blocks.find((stb) => stb.blockTagId === blockTagId);
